<template>
    <div class="module">
        <header class="module__header--with-disclaimer">
            <a
                class="back-btn"
                @click="goBack('relays')"
            >
                <span class="icon-chevron-left" />
            </a>
            <h1 class="has-back-btn">Bulk Upload Relays</h1>
            <p class="disclaimer">
                When importing radios that already exist in the organization, include only the Radio Serial # and name.
                Leave all other radio fields blank.
            </p>
        </header>

        <section class="module__actions">
            <button
                v-if="validated"
                :class="{ active: submitted }"
                class="btn btn-primary"
                data-test-id="import-relays-button"
                @click="importRelays()"
            >
                <div>Import {{ relays.length }} Relays</div>
            </button>
            <a
                v-else
                :href="downloadUrl(templateFilename)"
                class="btn"
                data-test-id="download-template-button"
                download
            >
                <span class="icon-export"></span> Download Import Template
            </a>
        </section>

        <section class="module__body">
            <form
                v-if="!validated"
                @submit.prevent="upload"
            >
                <div class="form-group">
                    <file-upload
                        :url="`${organizationSlug}/relays`"
                        accept=".csv"
                        @error="(formErrorResponse) => (errorResponse = formErrorResponse)"
                        @success="tempUploadComplete"
                    >
                    </file-upload>
                </div>

                <form-errors :response="errorResponse"></form-errors>

                <div class="form-group">
                    <button
                        :class="{ active: submitted }"
                        :disabled="!tempFileUploaded"
                        class="btn btn-primary"
                        data-test-id="upload-button"
                        type="submit"
                    >
                        <div>Upload</div>
                    </button>
                </div>
            </form>

            <p
                v-if="rowErrorLength"
                class="bulk-import-error-notification"
                data-test-id="error-summary"
            >
                {{ rowErrorLength }} of {{ relays.length }} rows had errors. Please review them below and re-upload your
                CSV file.
            </p>

            <table
                v-if="relays.length"
                class="index index-view index-import"
                data-test-id="import-preview-table"
            >
                <thead>
                    <tr>
                        <th scope="col">Row</th>
                        <th scope="col">Type</th>
                        <th scope="col">Name</th>
                        <th scope="col">Tower Mounted</th>
                        <th scope="col">Customer Asset #</th>
                        <th scope="col">Relay Serial #</th>
                        <th scope="col">Install Date</th>
                        <th scope="col">Installer Name</th>
                        <th scope="col">Latitude</th>
                        <th scope="col">Longitude</th>
                        <th scope="col">Elevation</th>
                        <th scope="col">Description</th>
                        <th scope="col">Power Type</th>
                        <th scope="col">Solar Power Panel Type</th>
                        <th scope="col">Solar Power Wattage</th>
                        <th scope="col">AC Power Source Type</th>
                        <th scope="col">AC Power Voltage</th>
                        <th scope="col">AC Power Circuit Amps</th>
                        <th scope="col">Battery Type</th>
                        <th scope="col">Is Battery External</th>
                        <th scope="col">Radio 1 Type</th>
                        <th scope="col">Radio 1 Name</th>
                        <th scope="col">Radio 1 Serial #</th>
                        <th scope="col">Radio 1 Customer Asset #</th>
                        <th scope="col">Radio 1 Description</th>
                        <th scope="col">Radio 2 Type</th>
                        <th scope="col">Radio 2 Name</th>
                        <th scope="col">Radio 2 Serial #</th>
                        <th scope="col">Radio 2 Customer Asset #</th>
                        <th scope="col">Radio 2 Description</th>
                    </tr>
                </thead>
                <tbody>
                    <!-- eslint-disable vue/no-v-for-template-key -->
                    <template
                        v-for="(relay, index) in relays"
                        :key="`d${index}`"
                    >
                        <!-- eslint-enable -->
                        <tr
                            :class="{ odd: index % 2 !== 0, 'row-error': index in rowErrors }"
                            :data-test-id="`row-${index}`"
                        >
                            <td :data-test-id="`row-${index}-col-row`">{{ index + 1 }}</td>
                            <td :data-test-id="`row-${index}-col-type`">
                                {{ relay['type']
                                }}<span
                                    v-if="rowHasError('type', index)"
                                    class="has-error"
                                    >*</span
                                >
                            </td>
                            <td :data-test-id="`row-${index}-col-site_name`">
                                {{ relay['site_name']
                                }}<span
                                    v-if="rowHasError('site_name', index)"
                                    class="has-error"
                                    >*</span
                                >
                            </td>
                            <td :data-test-id="`row-${index}-col-is_tower_mounted`">
                                {{ relay['is_tower_mounted']
                                }}<span
                                    v-if="rowHasError('is_tower_mounted', index)"
                                    class="has-error"
                                    >*</span
                                >
                            </td>
                            <td :data-test-id="`row-${index}-col-asset_number`">
                                {{ relay['asset_number']
                                }}<span
                                    v-if="rowHasError('asset_number', index)"
                                    class="has-error"
                                    >*</span
                                >
                            </td>
                            <td :data-test-id="`row-${index}-col-relay_serial_number`">
                                {{ relay['relay_serial_number']
                                }}<span
                                    v-if="rowHasError('relay_serial_number', index)"
                                    class="has-error"
                                    >*</span
                                >
                            </td>
                            <td :data-test-id="`row-${index}-col-installed_at`">
                                {{ relay['installed_at']
                                }}<span
                                    v-if="rowHasError('installed_at', index)"
                                    class="has-error"
                                    >*</span
                                >
                            </td>
                            <td :data-test-id="`row-${index}-col-installed_by`">
                                {{ relay['installed_by']
                                }}<span
                                    v-if="rowHasError('installed_by', index)"
                                    class="has-error"
                                    >*</span
                                >
                            </td>
                            <td :data-test-id="`row-${index}-col-latitude`">
                                {{ relay['latitude']
                                }}<span
                                    v-if="rowHasError('latitude', index)"
                                    class="has-error"
                                    >*</span
                                >
                            </td>
                            <td :data-test-id="`row-${index}-col-longitude`">
                                {{ relay['longitude']
                                }}<span
                                    v-if="rowHasError('longitude', index)"
                                    class="has-error"
                                    >*</span
                                >
                            </td>
                            <td :data-test-id="`row-${index}-col-altitude`">
                                {{ relay['altitude']
                                }}<span
                                    v-if="rowHasError('altitude', index)"
                                    class="has-error"
                                    >*</span
                                >
                            </td>
                            <td :data-test-id="`row-${index}-col-description`">
                                {{ relay['description']
                                }}<span
                                    v-if="rowHasError('description', index)"
                                    class="has-error"
                                    >*</span
                                >
                            </td>
                            <td :data-test-id="`row-${index}-col-power_type`">
                                {{ relay['power_type']
                                }}<span
                                    v-if="rowHasError('description', index)"
                                    class="has-error"
                                    >*</span
                                >
                            </td>
                            <td :data-test-id="`row-${index}-col-solar_power_panel_type`">
                                {{ relay['solar_power_panel_type']
                                }}<span
                                    v-if="rowHasError('solar_power_panel_type', index)"
                                    class="has-error"
                                    >*</span
                                >
                            </td>
                            <td :data-test-id="`row-${index}-col-solar_power_wattage`">
                                {{ relay['solar_power_wattage']
                                }}<span
                                    v-if="rowHasError('solar_power_wattage', index)"
                                    class="has-error"
                                    >*</span
                                >
                            </td>
                            <td :data-test-id="`row-${index}-col-ac_power_source_type`">
                                {{ relay['ac_power_source_type']
                                }}<span
                                    v-if="rowHasError('ac_power_source_type', index)"
                                    class="has-error"
                                    >*</span
                                >
                            </td>
                            <td :data-test-id="`row-${index}-col-ac_power_voltage`">
                                {{ relay['ac_power_voltage']
                                }}<span
                                    v-if="rowHasError('ac_power_voltage', index)"
                                    class="has-error"
                                    >*</span
                                >
                            </td>
                            <td :data-test-id="`row-${index}-col-ac_power_circuit_amps`">
                                {{ relay['ac_power_circuit_amps']
                                }}<span
                                    v-if="rowHasError('ac_power_circuit_amps', index)"
                                    class="has-error"
                                    >*</span
                                >
                            </td>
                            <td :data-test-id="`row-${index}-col-battery_type`">
                                {{ relay['battery_type']
                                }}<span
                                    v-if="rowHasError('battery_type', index)"
                                    class="has-error"
                                    >*</span
                                >
                            </td>
                            <td :data-test-id="`row-${index}-col-is_battery_external`">
                                {{ relay['is_battery_external']
                                }}<span
                                    v-if="rowHasError('is_battery_external', index)"
                                    class="has-error"
                                    >*</span
                                >
                            </td>
                            <td :data-test-id="`row-${index}-col-radio_1_type`">
                                {{ relay['radio_1_type']
                                }}<span
                                    v-if="rowHasError('radio_1_type', index)"
                                    class="has-error"
                                    >*</span
                                >
                            </td>
                            <td :data-test-id="`row-${index}-col-radio_1_name`">
                                {{ relay['radio_1_name']
                                }}<span
                                    v-if="rowHasError('radio_1_name', index)"
                                    class="has-error"
                                    >*</span
                                >
                            </td>
                            <td :data-test-id="`row-${index}-col-radio_1_serial`">
                                {{ relay['radio_1_serial']
                                }}<span
                                    v-if="rowHasError('radio_1_serial', index)"
                                    class="has-error"
                                    >*</span
                                >
                            </td>
                            <td :data-test-id="`row-${index}-col-radio_1_asset_number`">
                                {{ relay['radio_1_asset_number']
                                }}<span
                                    v-if="rowHasError('radio_1_asset_number', index)"
                                    class="has-error"
                                    >*</span
                                >
                            </td>
                            <td :data-test-id="`row-${index}-col-radio_1_description`">
                                {{ relay['radio_1_description']
                                }}<span
                                    v-if="rowHasError('radio_1_description', index)"
                                    class="has-error"
                                    >*</span
                                >
                            </td>
                            <td :data-test-id="`row-${index}-col-radio_2_type`">
                                {{ relay['radio_2_type']
                                }}<span
                                    v-if="rowHasError('radio_2_type', index)"
                                    class="has-error"
                                    >*</span
                                >
                            </td>
                            <td :data-test-id="`row-${index}-col-radio_2_name`">
                                {{ relay['radio_2_name']
                                }}<span
                                    v-if="rowHasError('radio_2_name', index)"
                                    class="has-error"
                                    >*</span
                                >
                            </td>
                            <td :data-test-id="`row-${index}-col-radio_2_serial`">
                                {{ relay['radio_2_serial']
                                }}<span
                                    v-if="rowHasError('radio_2_serial', index)"
                                    class="has-error"
                                    >*</span
                                >
                            </td>
                            <td :data-test-id="`row-${index}-col-radio_2_asset_number`">
                                {{ relay['radio_2_asset_number']
                                }}<span
                                    v-if="rowHasError('radio_2_asset_number', index)"
                                    class="has-error"
                                    >*</span
                                >
                            </td>
                            <td :data-test-id="`row-${index}-col-radio_2_description`">
                                {{ relay['radio_2_description']
                                }}<span
                                    v-if="rowHasError('radio_2_description', index)"
                                    class="has-error"
                                    >*</span
                                >
                            </td>
                        </tr>

                        <tr
                            v-if="rowErrorLength && rowErrors[index]"
                            :key="`e${index}`"
                            :class="{ odd: index % 2 !== 0 }"
                            data-test-id="import-errors-row"
                        >
                            <td colspan="16">
                                <ul class="bulk-import-errors">
                                    <li
                                        v-for="(error, errorIndex) in formattedRowErrors[index]"
                                        :key="errorIndex"
                                    >
                                        {{ error }}
                                    </li>
                                </ul>
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </section>
    </div>
</template>

<script>
import * as _ from 'lodash'
import FileUpload from '@/components/FileUpload/index.vue'
import FormErrors from '@/components/FormErrors.vue'
import canToast from '@/mixins/canToast'
import hasBackButton from '@/mixins/hasBackButton'
import hasDownloadableFile from '@/mixins/hasDownloadableFile'
import needsOrganizationSlug from '@/mixins/needsOrganizationSlug'

export default {
    components: {
        FileUpload,
        FormErrors,
    },

    mixins: [canToast, hasBackButton, hasDownloadableFile, needsOrganizationSlug],

    data() {
        return {
            filenames: {},
            validated: false,
            relays: [],
            errorResponse: null,
            rowErrors: {},
            submitted: false,
            tempFileUploaded: false,
        }
    },

    computed: {
        formattedRowErrors() {
            const formatted = {}

            _.forEach(this.rowErrors, (rowError, index) => {
                let errorList = []

                _.forEach(rowError, (errors, property) => {
                    errorList = errorList.concat(errors)
                })

                formatted[index] = errorList
            })

            return formatted
        },

        rowErrorLength() {
            return Object.keys(this.rowErrors).length
        },

        templateFilename() {
            return 'portal-bulk-relay-upload-template.csv'
        },
    },

    methods: {
        tempUploadComplete(filenames) {
            this.filenames = filenames
            this.errorResponse = null
            this.tempFileUploaded = true
        },

        upload() {
            if (!this.tempFileUploaded || this.submitted) {
                return
            }
            this.submitted = true
            this.$http
                .post(`${this.organizationSlug}/relays/validate-upload`, {
                    temp_filename: this.filenames.temp,
                    original_filename: this.filenames.original,
                    organization_id: this.organizationId,
                })
                .then(({ data }) => {
                    this.validated = true
                    this.submitted = false
                    this.relays = data
                    this.errorResponse = null
                    this.title = 'Confirm Relays Import'
                    this.toast('Relays ready to import!')
                })
                .catch((error) => {
                    const { type } = error.response.data
                    if (type === 'row-error') {
                        // handle errors in row data
                        const response = error.response.data
                        this.errorResponse = null
                        this.rowErrors = response.errors
                        this.relays = response.relays
                    } else {
                        // handle generic error
                        this.errorResponse = error.response
                        this.rowErrors = {}
                        this.relays = []
                    }
                    this.submitted = false
                })
        },

        importRelays() {
            if (this.submitted) {
                return
            }

            this.submitted = true
            this.$http
                .post(`${this.organizationSlug}/relays/import-upload`, {
                    temp_filename: this.filenames.temp,
                    relay_count: this.relays.length,
                    organization_id: this.organizationId,
                })
                .then(({ data }) => {
                    this.toast('Relays successfully imported!')
                    this.$router.push(`/${this.organizationSlug}/relays`)
                })
                .catch((error) => {
                    this.toast('There was a problem importing the relays.', 'error')
                    this.submitted = false
                    this.errorResponse = { data: { errors: ['There was a problem importing the relays.'] } }
                })
        },

        rowHasError(type, index) {
            return index in this.rowErrors && type in this.rowErrors[index]
        },
    },
}
</script>

<style scoped>
.bulk-import-error-notification {
    margin: 45px 0 30px 0;
}

.bulk-import-errors {
    font-family: 'Avenir Heavy';
    font-size: 0.9125em;
}

.bulk-import-errors li {
    margin-bottom: 12px;
}

.bulk-import-errors,
.has-error {
    color: #f96a62;
}

table.index tbody tr:nth-child(even) {
    background-color: #434a4d;
}

.index-import tbody tr {
    background-color: #434a4d;
}

.index-import tbody tr.row-error {
    border-bottom: 0;
}

.index-import tbody tr.odd {
    background-color: #484f52;
}

.module__body {
    overflow-x: scroll;
}
</style>
